import { useEffect, useMemo, useState } from 'react';
import { Folder, FileText, Youtube, Download as DLIcon, Search, ArrowRight, Home, ExternalLink } from 'lucide-react';
import api from '../../utils/api';
import { openFileNode } from '../../utils/fileDownload';
import { sortFileNodes } from '../../utils/fileNodeSort';

/** Sales-side Downloads — folder browser; sort order is set by admin. */
export default function SalesDownloads() {
    const [tree, setTree] = useState([]);
    const [parent, setParent] = useState(null);
    const [crumbs, setCrumbs] = useState([]);
    const [q, setQ] = useState('');
    const [sortBy, setSortBy] = useState('date_desc');

    useEffect(() => {
        api.get('/files/tree').then(r => {
            setTree(r.data.items || []);
            if (r.data.sortBy) setSortBy(r.data.sortBy);
        });
    }, []);

    const childrenOf = (parentId) => tree.filter(n => String(n.parent || '') === String(parentId || ''));

    const visible = useMemo(() => {
        let items;
        if (q.trim()) {
            const term = q.toLowerCase();
            items = tree.filter(n => n.name.toLowerCase().includes(term));
        } else {
            items = childrenOf(parent);
        }
        return sortFileNodes(items, sortBy);
    }, [tree, parent, q, sortBy]);

    const enter = (folder) => {
        setParent(folder._id);
        setCrumbs(c => [...c, folder]);
        setQ('');
    };
    const goTo = (idx) => {
        const next = crumbs.slice(0, idx + 1);
        setCrumbs(next);
        setParent(next.length ? next[next.length - 1]._id : null);
    };
    const goHome = () => { setCrumbs([]); setParent(null); };

    const previewFile = async (n) => {
        try {
            await openFileNode(n, { preview: true });
        } catch { /* toasted */ }
    };

    const downloadFile = async (n) => {
        try {
            await openFileNode(n, { preview: false });
        } catch { /* toasted */ }
    };

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-extrabold">Downloads</h1>
                <p className="text-sm text-ink-500">Brochures, training, price lists, videos — kept current by admin.</p>
            </div>

            {/* Search */}
            <div className="relative max-w-xl">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                <input className="ag-input pl-9 w-full" placeholder="Search files or folders…" value={q} onChange={e => setQ(e.target.value)} />
            </div>

            {/* Breadcrumb */}
            {!q && (
                <div className="flex flex-wrap items-center gap-1.5 text-sm">
                    <button className="flex items-center gap-1 ag-link" onClick={goHome}><Home className="h-4 w-4" /> Home</button>
                    {crumbs.map((c, i) => (
                        <span key={c._id} className="flex items-center gap-1.5">
                            <ArrowRight className="h-3.5 w-3.5 text-ink-300" />
                            <button className="ag-link truncate max-w-[160px]" onClick={() => goTo(i)}>{c.name}</button>
                        </span>
                    ))}
                </div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-5">
                {visible.map(n => (
                    <div key={n._id}
                         className="ag-card p-4 hover:shadow-soft hover:ring-2 hover:ring-brand-teal/20 transition flex flex-col"
                         onClick={() => {
                             if (n.type === 'folder') enter(n);
                         }}>
                        {n.type === 'folder' && <Folder  className="h-10 w-10 text-brand-yellow mx-auto cursor-pointer" />}
                        {n.type === 'file'   && <FileText className="h-10 w-10 text-brand-red mx-auto" />}
                        {n.type === 'youtube'&& (
                            <div className="aspect-video rounded-lg overflow-hidden bg-ink-900">
                                <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${n.youtubeId}`} title={n.name} allowFullScreen />
                            </div>
                        )}
                        <div className={`mt-3 text-center text-sm font-semibold line-clamp-2 ${n.type === 'folder' ? 'cursor-pointer' : ''}`}>{n.name}</div>
                        {n.description && <div className="mt-1 text-[11px] text-ink-500 text-center line-clamp-2">{n.description}</div>}
                        {n.type === 'file' && (
                            <div className="mt-3 flex gap-2">
                                <button type="button"
                                   onClick={(e) => { e.stopPropagation(); downloadFile(n); }}
                                   className="ag-btn-primary text-xs flex-1 justify-center">
                                    <DLIcon className="h-3.5 w-3.5" /> Download
                                </button>
                                <button type="button"
                                   onClick={(e) => { e.stopPropagation(); previewFile(n); }}
                                   className="ag-btn-ghost text-xs"
                                   title="View in new tab">
                                    <ExternalLink className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}
                        {n.type === 'youtube' && (
                            <a
                                href={n.youtubeUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="ag-btn-primary text-xs justify-center mt-3"
                            >
                                <Youtube className="h-3.5 w-3.5" /> Watch
                            </a>
                        )}
                    </div>
                ))}
                {visible.length === 0 && (
                    <div className="col-span-full text-center text-sm text-ink-500 py-12">
                        {q ? `No results for "${q}"` : 'This folder is empty.'}
                    </div>
                )}
            </div>
        </div>
    );
}
